import { useRef, useState } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import { L, LavDropdown } from '../ui/LavDropdown'
import { StIc, StToggle } from './stBits'

/**
 * StoreHoursEditor — per-day store hours per the Claude Design Settings spec
 * ("Settings · Business" desktop rows / mobile accordion).
 *
 * A day is a list of open intervals ("spans"); the gap between two spans is
 * simply closed. Times stay in the app's stored shape — "HH:mm" strings on
 * `weeklyHours` keys "0"(Sun)…"6"(Sat) — and each picker is clamped against
 * its neighbours so overlapping hours are unreachable.
 *
 * The + button appends the design's "next span": starts one hour after the
 * day's current close, runs 3 hours, clamped to the end-of-day boundary
 * (11:30 PM) — so a late close yields a short late-night span, and once
 * there's no room left the + disables.
 */

// Design order: Monday first. Keys match the stored weekly_hours shape.
const DAY_ROWS = [
	['1', 'Monday'], ['2', 'Tuesday'], ['3', 'Wednesday'], ['4', 'Thursday'],
	['5', 'Friday'], ['6', 'Saturday'], ['0', 'Sunday'],
]

const strToH = (s) => {
	const [h, m] = String(s || '0:0').split(':').map(Number)
	return (h || 0) + (m || 0) / 60
}
const hToStr = (h) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h - Math.floor(h)) * 60)).padStart(2, '0')}`
const fmtTime = (s) => dayjs(`1970-01-01T${s}`).format('h:mm A')

// Half-hour steps across the whole day — open/close times are a free pick.
// LAST_H is the latest selectable time (11:30 PM); a stored value outside the
// grid still renders because the pickers merge the current value in.
const LAST_H = 23.5
const TIME_OPTS = (() => {
	const a = []
	for (let h = 0; h <= LAST_H; h += 0.5) a.push(hToStr(h))
	return a
})()

const sortIntervals = (list) => [...list].sort((a, b) => (a.start < b.start ? -1 : 1))
const dayHours = (list) => list.reduce((s, iv) => s + Math.max(0, strToH(iv.end) - strToH(iv.start)), 0)
const fmtHrs = (n) => (Number.isInteger(n) ? n : n.toFixed(1))

export const canAddInterval = (list) => !list.length || strToH(list[list.length - 1].end) <= LAST_H - 1

export const nextInterval = (list) => {
	const last = list[list.length - 1]
	const start = last ? Math.min(strToH(last.end) + 1, LAST_H - 0.5) : 9
	return { start: hToStr(start), end: hToStr(Math.min(start + 3, LAST_H)) }
}

// "9:00 – 12:30 PM" — drop the first meridiem when both ends share it.
const fmtSpan = (iv) => {
	const a = fmtTime(iv.start), b = fmtTime(iv.end)
	return (a.slice(-2) === b.slice(-2) ? a.slice(0, -3) : a) + ' – ' + b
}

export const hoursSummary = (weeklyHours, isDayOpen) => {
	let days = 0
	let hrs = 0
	DAY_ROWS.forEach(([k]) => {
		if (!isDayOpen(k)) return
		days += 1
		hrs += dayHours(weeklyHours[k] || [])
	})
	return { days, hrs: fmtHrs(hrs) }
}

/** "N days open" / "H hrs / week" pill (design st-countpill). */
export function StCountPill({ strong, rest }) {
	return (
		<Box sx={{
			display: 'inline-flex', alignItems: 'baseline', gap: '5px', bgcolor: L.primarySoft,
			color: L.primaryDeep, borderRadius: '99px', p: '5px 12px', fontSize: '12px',
			fontWeight: 700, whiteSpace: 'nowrap', fontFamily: L.font,
		}}>
			<Box component="strong" sx={{ fontSize: '14.5px', fontWeight: 800 }}>{strong}</Box>
			{rest}
		</Box>
	)
}

// Clamped time dropdown (design st-timesel). min/max are "HH:mm" bounds; the
// current value is kept in the list even when outside the standard steps.
function HoursTimeSelect({ value, onChange, min = null, max = null }) {
	const opts = TIME_OPTS.filter((t) => (min == null || t >= min) && (max == null || t <= max))
	if (value && !opts.includes(value)) opts.push(value)
	opts.sort()
	return (
		<LavDropdown
			placeholder="Time"
			value={value}
			options={opts.map((t) => ({ id: t, label: fmtTime(t) }))}
			onSelect={(v) => { if (v) onChange(v) }}
		/>
	)
}

const toolBtnSx = {
	width: 30, height: 30, flex: 'none', border: 'none', borderRadius: '9px',
	bgcolor: 'transparent', color: L.muted, display: 'flex', alignItems: 'center',
	justifyContent: 'center', cursor: 'pointer', fontFamily: L.font, p: 0,
	'&:hover': { color: L.primaryDeep, bgcolor: L.primarySoft },
	'&:disabled': { opacity: 0.35, pointerEvents: 'none' },
}

const delBtnSx = {
	width: 26, height: 26, flex: 'none', border: 'none', borderRadius: '8px',
	bgcolor: 'transparent', color: L.muted, display: 'flex', alignItems: 'center',
	justifyContent: 'center', cursor: 'pointer', p: 0,
	'&:hover': { color: '#BE4B45', bgcolor: '#FBECEB' },
}

export default function StoreHoursEditor({ weeklyHours, setWeeklyHours, isDayOpen, setDayOpen, stacked = false }) {
	// Mobile accordion: which day row is expanded.
	const [openDayKey, setOpenDayKey] = useState(null)
	// Hours a closed day had before its toggle went off — restored on re-open,
	// so flipping a day off and back doesn't lose its spans.
	const stashRef = useRef({})

	const setDay = (key, list) => setWeeklyHours((prev) => ({ ...prev, [key]: sortIntervals(list) }))
	const intervalsFor = (key) => sortIntervals(weeklyHours[key] || [])

	const updateInterval = (key, index, patch) => {
		const list = intervalsFor(key)
		setDay(key, list.map((iv, i) => (i === index ? { ...iv, ...patch } : iv)))
	}
	const removeInterval = (key, index) => {
		const list = intervalsFor(key).filter((_, i) => i !== index)
		if (list.length) setDay(key, list)
	}
	const addInterval = (key) => {
		const list = intervalsFor(key)
		if (!canAddInterval(list)) return
		setDay(key, [...list, nextInterval(list)])
	}
	const copyToOpenDays = (key) => {
		const src = intervalsFor(key)
		setWeeklyHours((prev) => {
			const out = { ...prev }
			DAY_ROWS.forEach(([k]) => {
				if (k !== key && isDayOpen(k)) out[k] = src.map((iv) => ({ ...iv }))
			})
			return out
		})
	}
	const toggleDay = (key) => {
		if (isDayOpen(key)) {
			stashRef.current[key] = weeklyHours[key] || []
			setDayOpen(key, false)
			setDay(key, [])
		} else {
			setDayOpen(key, true)
			const restored = stashRef.current[key]
			setDay(key, restored && restored.length ? restored : [{ start: '09:00', end: '17:00' }])
		}
	}

	// One span row: clamped start/end pickers + remove (when several spans).
	const spanRow = (key, iv, i, list, mobile) => {
		const prev = list[i - 1]
		const next = list[i + 1]
		return (
			<Box key={`${key}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: mobile ? '8px' : '9px' }}>
				<Box sx={mobile ? { flex: 1, minWidth: 0 } : { width: 118, flex: 'none' }}>
					<HoursTimeSelect
						value={iv.start}
						min={prev ? hToStr(strToH(prev.end) + 0.5) : null}
						max={hToStr(strToH(iv.end) - 0.5)}
						onChange={(v) => updateInterval(key, i, { start: v })}
					/>
				</Box>
				<Box component="span" sx={{ fontSize: mobile ? '12px' : '13px', fontWeight: 600, color: L.muted, flex: 'none' }}>to</Box>
				<Box sx={mobile ? { flex: 1, minWidth: 0 } : { width: 118, flex: 'none' }}>
					<HoursTimeSelect
						value={iv.end}
						min={hToStr(strToH(iv.start) + 0.5)}
						max={next ? hToStr(strToH(next.start) - 0.5) : null}
						onChange={(v) => updateInterval(key, i, { end: v })}
					/>
				</Box>
				{list.length > 1 && (
					<Box
						component="button" type="button" aria-label="Remove these hours"
						onClick={() => removeInterval(key, i)}
						sx={{ ...delBtnSx, ...(mobile ? { width: 36, height: 36, mr: '-4px' } : {}) }}
					>
						<StIc name="x" size={mobile ? 15 : 13} sw={1.8} />
					</Box>
				)}
			</Box>
		)
	}

	// ── mobile: one compact summary row per day, tapped day expands (stmh-*) ──
	if (stacked) {
		return (
			<Box sx={{ p: '4px 10px 8px', fontFamily: L.font }}>
				{DAY_ROWS.map(([key, label]) => {
					const on = isDayOpen(key)
					const list = intervalsFor(key)
					const expanded = openDayKey === key
					return (
						<Box key={key} sx={{
							borderBottom: `1px solid ${L.surface}`,
							'&:last-child': { borderBottom: 'none' },
							...(expanded ? {
								m: '6px 0', borderRadius: '15px', border: `1.5px solid ${L.border}`,
								borderBottomColor: L.border, bgcolor: '#FFFFFF',
								boxShadow: '0 2px 10px rgba(95,78,194,0.06)',
							} : {}),
						}}>
							<Box
								component="button" type="button" aria-expanded={expanded}
								onClick={() => setOpenDayKey(expanded ? null : key)}
								sx={{
									display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
									minHeight: 56, p: expanded ? '11px 12px 6px' : '11px 8px',
									border: 'none', bgcolor: 'transparent', cursor: 'pointer',
									fontFamily: 'inherit', textAlign: 'left',
								}}
							>
								<Box component="span" sx={{ flex: 'none', width: 88, fontSize: '14.5px', fontWeight: 750, color: on ? L.text : '#BBB4CE' }}>
									{label}
								</Box>
								<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
									{on && list.length > 0 ? (
										<Box component="strong" sx={{ fontSize: '13.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: L.text }}>
											{fmtSpan(list[0])}
										</Box>
									) : (
										<Box component="em" sx={{ fontStyle: 'normal', fontSize: '13.5px', fontWeight: 650, color: '#BBB4CE' }}>Closed</Box>
									)}
									{on && list.length > 1 && (
										<Box component="span" sx={{
											maxWidth: '100%', fontSize: '11.5px', fontWeight: 600, color: L.muted,
											whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
										}}>
											{list.slice(1).map(fmtSpan).join(' · ')}
										</Box>
									)}
								</Box>
								<Box component="span" sx={{
									display: 'flex', flex: 'none', color: expanded ? L.primary : '#C4BDD6',
									transition: 'transform .16s ease', transform: expanded ? 'rotate(90deg)' : 'none',
								}}>
									<StIc name="chevright" size={14} sw={1.8} />
								</Box>
							</Box>
							{expanded && (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px', p: '2px 12px 14px' }}>
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontSize: '13.5px', fontWeight: 650, color: L.text }}>
										<span>Open this day</span>
										<StToggle on={on} onChange={() => toggleDay(key)} />
									</Box>
									{on && (
										<>
											{list.map((iv, i) => spanRow(key, iv, i, list, true))}
											<Box
												component="button" type="button"
												disabled={!canAddInterval(list)}
												onClick={() => addInterval(key)}
												sx={{
													display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
													height: 44, border: '1.5px dashed #D6CFEA', borderRadius: '13px',
													bgcolor: 'transparent', fontFamily: 'inherit', fontSize: '13.5px',
													fontWeight: 700, color: L.primaryDeep, cursor: 'pointer',
													'&:active': { bgcolor: L.primarySoft },
													'&:disabled': { opacity: 0.4 },
												}}
											>
												<StIc name="plus" size={13} sw={2.2} /><span>Add hours</span>
											</Box>
										</>
									)}
								</Box>
							)}
						</Box>
					)
				})}
			</Box>
		)
	}

	// ── desktop: every day on one wide row (st-hourlist) ──
	return (
		<Box sx={{ p: '6px 12px 10px', fontFamily: L.font }}>
			{DAY_ROWS.map(([key, label]) => {
				const on = isDayOpen(key)
				const list = intervalsFor(key)
				return (
					<Box key={key} sx={{ borderBottom: `1px solid ${L.surface}`, '&:last-child': { borderBottom: 'none' } }}>
						<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', p: '9px 8px' }}>
							<Box component="span" sx={{ width: 96, flex: 'none', fontSize: '13.5px', fontWeight: 700, lineHeight: '42px', color: on ? L.text : '#BBB4CE' }}>
								{label}
							</Box>
							<Box sx={{ mt: '8px' }}>
								<StToggle on={on} onChange={() => toggleDay(key)} />
							</Box>
							{on ? (
								<>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
										{list.map((iv, i) => spanRow(key, iv, i, list, false))}
									</Box>
									<Box sx={{ flex: 1 }} />
									<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 650, color: L.muted, whiteSpace: 'nowrap', lineHeight: '42px' }}>
										{fmtHrs(dayHours(list))} {dayHours(list) === 1 ? 'hr' : 'hrs'} bookable
									</Box>
									<Box
										component="button" type="button" title="Copy these hours to every open day"
										aria-label={`Copy ${label} hours to all open days`}
										onClick={() => copyToOpenDays(key)}
										sx={{ ...toolBtnSx, mt: '6px' }}
									>
										<StIc name="copy" size={14} sw={1.6} />
									</Box>
									<Box
										component="button" type="button" disabled={!canAddInterval(list)}
										title="Add hours — split the day to close in between"
										aria-label={`Add another set of hours on ${label}`}
										onClick={() => addInterval(key)}
										sx={{ ...toolBtnSx, mt: '6px', boxShadow: 'inset 0 0 0 1px #D6CFEA', '&:hover': { color: L.primaryDeep, bgcolor: L.primarySoft, boxShadow: `inset 0 0 0 1px ${L.primary}` } }}
									>
										<StIc name="plus" size={14} sw={2} />
									</Box>
								</>
							) : (
								<>
									<Box component="span" sx={{ fontSize: '13px', fontWeight: 600, color: '#BBB4CE', lineHeight: '42px' }}>Closed</Box>
									<Box sx={{ flex: 1 }} />
								</>
							)}
						</Box>
					</Box>
				)
			})}
		</Box>
	)
}
